import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from "react-redux";
import Select from 'react-select';
import Swal from "sweetalert2";
import { Modal, Pagination } from 'react-bootstrap';
import { getRolesAction, deleteRoleAction, createRoleAction, updateRoleAction } from '../../store/actions/role/RoleActions';
import { getAllPoliciesAction } from '../../store/actions/policy/PolicyActions';

const Role = () => {
	const dispatch = useDispatch();

	const { roles, loading } = useSelector((state) => state.roles);
	const roleRows = roles?.rows || [];
	const totalPages = roles?.pages || 1;
	const { policies } = useSelector((state) => state.policies);
	const policyRows = policies?.rows || [];
	const [formErrors, setFormErrors] = useState({});
	const [searchTerm, setSearchTerm] = useState('');

	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize] = useState(10);
	const [addCard, setAddCard] = useState(false);

	const [addFormData, setAddFormData] = useState({
		name: '',
		policies: [],
		isEnabled: true
	});

	const [showFilterPopup, setShowFilterPopup] = useState(false);
	const filterButtonRef = useRef(null);
	const filterPopupRef = useRef(null);

	const [filterValues, setFilterValues] = useState({
		name: '',
		email: '',
		policies: '',
		status: ''
	});

	const [editMode, setEditMode] = useState(false);
	const [editRoleData, setEditRoleData] = useState(null);

	const handleAddFormChange = (input, customValue) => {
		if (input?.target) {
			// Native input event
			const { name, value } = input.target;
			setAddFormData((prev) => ({
				...prev,
				[name]: name === "isEnabled" ? value === "true" : value
			}));

			setFormErrors((prevErrors) => ({
				...prevErrors,
				[name]: ''
			}));
		} else {
			// Custom input like react-select
			const { name, value } = customValue;
			setAddFormData((prev) => ({
				...prev,
				[name]: value
			}));

			setFormErrors((prevErrors) => ({
				...prevErrors,
				[name]: ''
			}));
		}
	};

	const handleAddFormSubmit = async (event) => {
		event.preventDefault();

		const { name, policies } = addFormData;
		const errors = {};

		if (!name.trim()) errors.name = "Name is required.";
		if (!Array.isArray(policies) || policies.length === 0) {
			errors.policies = "At least one policy must be selected.";
		}
		console.log(errors)
		setFormErrors(errors);

		if (Object.keys(errors).length > 0) return;

		const rolePayload = {
			name,
			policies,
			isEnabled: addFormData.isEnabled
		};

		try {
			let response;
			if (editMode && editRoleData?._id) {
				response = await dispatch(updateRoleAction(editRoleData._id, rolePayload));
			} else {
				response = await dispatch(createRoleAction(rolePayload));
			}

			if (response.data.success) {
				Swal.fire({ icon: 'success', title: 'Success!', text: editMode ? 'Role updated' : 'Role added' });
				resetForm();
				dispatch(getRolesAction(currentPage, pageSize, searchTerm));
			} else {
				Swal.fire({ icon: 'error', title: 'Error', text: response.data?.msg || 'Something went wrong' });
			}
		} catch (error) {
			Swal.fire({ icon: 'error', title: 'Error', text: error?.response?.data?.msg || 'Unexpected error occurred' });
		}
	};

	const handleDelete = async (userId) => {
		const result = await Swal.fire({
			title: "Are you sure?",
			text: "You won't be able to revert this!",
			icon: "warning",
			showCancelButton: true,
			confirmButtonColor: "#3085d6",
			cancelButtonColor: "#d33",
			confirmButtonText: "Yes, delete it!"
		});

		if (result.isConfirmed) {
			const response = await dispatch(deleteRoleAction(userId));
			console.log(response)
			if (response.data.success) {
				Swal.fire("Deleted!", "The role has been deleted.", "success");
				dispatch(getRolesAction(currentPage, pageSize, searchTerm));
			} else {
				Swal.fire("Failed!", response.data.msg, "warning");
			}
		}
	};

	const handleEdit = (role) => {
		setEditMode(true);
		setEditRoleData(role);
		setFormErrors({});
		setAddFormData({
			name: role.name,
			policies: role.policies.map(policy => policy._id),
			isEnabled: role.isEnabled
		});
		setAddCard(true);
	};

	const resetForm = () => {
		setAddCard(false);
		setEditMode(false);
		setEditRoleData(null);
		setAddFormData({
			name: '',
			policies: [],
			isEnabled: true
		});
	};

	const searchRole = () => {
		dispatch(getRolesAction(currentPage, pageSize, searchTerm));
	}

	const buildFilterQuery = () => {
		const params = new URLSearchParams();

		if (filterValues.name) {
			params.append('filter_name', `regex-${filterValues.name}`);
		}

		if (filterValues.policies) {
			params.append('filter_policies', `in-${filterValues.policies}`); // Assuming role is the ID like '681aea98f5a25f1b4495fb30'
		}

		if (filterValues.status) {
			params.append('filter_isEnabled', filterValues.status); // Should be 'true' or 'false' string
		}

		return params.toString(); // Returns the full query string
	};

	const applyFilters = () => {
		const filterQuery = buildFilterQuery();
		console.log(filterQuery)
		dispatch(getRolesAction(currentPage, pageSize, searchTerm, filterQuery));
		setShowFilterPopup(false);
	};

	const resetFilters = () => {
		dispatch(getRolesAction(currentPage, pageSize, searchTerm));
		setFilterValues({ name: '', email: '', policies: '', status: '' });
		setShowFilterPopup(false);
	};

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
			filterPopupRef.current &&
			!filterPopupRef.current.contains(event.target) &&
			!filterButtonRef.current.contains(event.target)
			) {
			setShowFilterPopup(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	useEffect(() => {
		dispatch(getRolesAction(currentPage, pageSize, searchTerm));
	}, [dispatch, currentPage, pageSize, searchTerm]);

	useEffect(() => {
		dispatch(getAllPoliciesAction());
	}, [dispatch]);

	return (
		<>
			<div className="col-12">
				<Modal className="modal fade" show={addCard} onHide={resetForm}>
					<form onSubmit={handleAddFormSubmit}>
						<div className="modal-header">
							<h4 className="modal-title fs-20">{editMode ? 'Edit Role' : 'Add Role'}</h4>
							<button type="button" className="btn-close" onClick={resetForm}></button>
						</div>
						<div className="modal-body">
							<div className="add-contact-box">
								<div className="add-contact-content">
									<div className="form-group mb-3">
										<label>Name</label>
										<input type="text" className={`form-control ${formErrors.name ? 'is-invalid' : ''}`} name="name" value={addFormData.name} onChange={handleAddFormChange} placeholder="Name"  />
										{formErrors.name && <div className="invalid-feedback">{formErrors.name}</div>}
									</div>
									<div className="form-group mb-3">
										<label>Policies</label>
										<div className={`form-group ${formErrors.policies ? 'is-invalid' : ''}`}>
											<Select
												isMulti
												name="policies"
												options={policyRows.map(policy => ({
													value: policy._id,
													label: policy.name
												}))}
												value={addFormData.policies?.map(val => {
													const match = policyRows.find(p => p._id === val);
													return match ? { value: match._id, label: match.name } : null;
												}).filter(Boolean)}
												onChange={(selectedOptions) => {
													const values = selectedOptions.map(option => option.value);
													handleAddFormChange(null, { name: 'policies', value: values });
												}}
											/>
											{formErrors.policies && (
												<div className="invalid-feedback d-block">{formErrors.policies}</div>
											)}
										</div>
									</div>
									<div className="form-group mb-3">
										<label>Status</label>
										<select name="isEnabled" className={`form-control ${formErrors.isEnabled ? 'is-invalid' : ''}`} value={addFormData.isEnabled} onChange={handleAddFormChange}>
											<option value={true}>Enabled</option>
											<option value={false}>Disabled</option>
										</select>
										{formErrors.isEnabled && <div className="invalid-feedback">{formErrors.isEnabled}</div>}
									</div>
								</div>
							</div>
						</div>
						<div className="modal-footer">
							<button type="submit" className="btn btn-primary">{editMode ? "Update" : "Add"}</button>
							<button type="button" onClick={resetForm} className="btn btn-danger">Discard</button>
						</div>
					</form>
				</Modal>

				<div className="card">
					<div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-3">
						<h4 className="card-title mb-0">Role List</h4>
						
						<div className="input-group w-50">
							<input
								type="text"
								className="form-control"
								placeholder="Search by name..."
								onChange={(e) => setSearchTerm(e.target.value)} 
								onKeyDown={(e) => {
									if (e.key === 'Enter') {
										searchRole();
									}
								}}
							/>
							<span onClick={searchRole} className="input-group-text" style={{ cursor: 'pointer' }}>
								<i className="fa fa-search"></i>
							</span>
						</div>

						{/* Filter Icon */}
						<div className="position-relative">
							<button
								className="btn btn-outline-secondary shadow btn-xs sharp me-2"
								onClick={() => setShowFilterPopup((prev) => !prev)}
								ref={filterButtonRef}
							>
								<i className="fa fa-filter"></i>
							</button>

							{/* Filter Popup */}
							{showFilterPopup && (
								<div
									className="card p-3 shadow position-absolute"
									style={{
										right: '0',   
										zIndex: 1000,
										width: '300px',
										height: '245px'
									}}
									ref={filterPopupRef}
								>
									{/* Filter Fields */}
									<input
										type="text"
										className="form-control mb-2"
										placeholder="Name"
										value={filterValues.name}
										onChange={(e) => setFilterValues({ ...filterValues, name: e.target.value })}
									/>

									<select
										className="form-control mb-2"
										value={filterValues.policies}
										onChange={(e) => setFilterValues({ ...filterValues, policies: e.target.value })}
									>
										<option value="">All Policies</option>
										{policyRows.map((role) => (
											<option key={role._id} value={role._id}>{role.name}</option>
										))}
									</select>

									<select
										className="form-control mb-3"
										value={filterValues.status}
										onChange={(e) => setFilterValues({ ...filterValues, status: e.target.value })}
									>
										<option value="">All Statuses</option>
										<option value="true">Enabled</option>
										<option value="false">Disabled</option>
									</select>

									<div className="d-flex justify-content-between">
										<button className="btn btn-secondary" onClick={resetFilters}>Reset</button>
										<button className="btn btn-primary" onClick={applyFilters}>Apply</button>
									</div>
								</div>
							)}
						</div>

						<button className="btn btn-primary shadow btn-xs sharp me-2" onClick={() => setAddCard(true)}>
							<i className="fa fa-plus"></i>
						</button>
					</div>
					<div className="card-body">
						<div className="table-responsive">
							<table className="table table-striped">
								<thead>
									<tr>
										<th>Name</th>
										<th>Policies</th>
										<th>Enabled</th>
										<th>Action</th>
									</tr>
								</thead>
								<tbody>
									{loading ? (
										Array.from({ length: 5 }).map((_, idx) => (
											<tr key={idx}>
												<td colSpan="5">
													<div className="skeleton-loader" style={{ height: 30, width: '100%', background: '#eee', borderRadius: 4 }}></div>
												</td>
											</tr>
										))
									) : roleRows.length === 0 ? (
										<tr><td colSpan="5">No roles found.</td></tr>
									) : (
										roleRows.map((role) => (
											<tr key={role._id}>
												<td>{role.name}</td>
												<td>
													{role.policies?.map(policy => policy.name).join(', ')}
												</td>
												<td className={role.isEnabled ? 'text-success' : 'text-danger'}>
													{role.isEnabled ? 'True' : 'False'}
												</td>
												<td>
													<div className="d-flex">
														<button className="btn btn-secondary shadow btn-xs sharp me-2" onClick={() => handleEdit(role)}>
															<i className="fa fa-pencil"></i>
														</button>
														<button className="btn btn-danger shadow btn-xs sharp" onClick={() => handleDelete(role._id)}>
															<i className="fa fa-trash"></i>
														</button>
													</div>
												</td>
											</tr>
										))
									)}
								</tbody>
							</table>
						</div>

						<div className="d-flex justify-content-between align-items-center mt-4">
							<div></div> {/* Empty div to align right side content */}
							<div className="d-flex align-items-center gap-3">
								<span className="text-muted">Total Roles: {roles?.total || roleRows.length}</span>
								<Pagination className="pagination-gutter pagination-circle pagination mb-0">
									<Pagination.Prev
										disabled={currentPage === 1}
										onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
									/>
									{Array.from({ length: totalPages }, (_, i) => (
										<Pagination.Item
											key={i + 1}
											active={i + 1 === currentPage}
											onClick={() => setCurrentPage(i + 1)}
										>
											{i + 1}
										</Pagination.Item>
									))}
									<Pagination.Next
										disabled={currentPage === totalPages}
										onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
									/>
								</Pagination>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default Role;
